import { Injectable, Injector } from '@angular/core';
import { EntitlementService } from '../../application/services/entitlement.service';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LanguageService } from '../../application/services/language.service';

/**
 * AuthInterceptor — auto-logout on 401 Unauthorized
 * If the API returns 401, the token has expired or is invalid.
 * Clear session storage and redirect to /login.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /** Last time a NO_ENTITLEMENT toast was shown per module, so a page firing several calls shows one message. */
  private lastNoEntitlementToast: Record<string, number> = {};
  private lastRateLimitToast = 0;
  private lastScreenPermissionToast = 0;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private langService: LanguageService,
    private injector?: Injector
  ) {}

  /** The menu's modules are stale when the API refuses one (the plan changed since sign-in): reload them (lazily: no cycle via HttpClient). */
  private refreshEntitlements(): void {
    try {
      this.injector?.get(EntitlementService).load(true).subscribe();
    } catch {
      // not available (e.g. a test without the service): the next sign-in loads them
    }
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // ARAP-06: the customer / supplier portal has its own session and handles its own refusals (never the company sign-in).
    if (request.url.includes('/Portal/')) return next.handle(request);
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // SUB-01: after the end date the Manager is read-only (a refused change keeps the screen open); any other user is signed out.
        const code = (error.error as any)?.code;
        if (error.status === 403 && code === 'SUBSCRIPTION_ENDED_READ_ONLY') {
          sessionStorage.setItem('subscriptionEnded', '1');
          this.toastr.warning(this.langService.getCurrent() === 'ar'
            ? 'انتهى اشتراك شركتكم: يمكنكم عرض بياناتكم، ولا يمكن تعديل أي شيء حتى التجديد من صفحة اشتراكي.'
            : 'Your subscription has ended: you can view your data, but nothing can be changed until you renew on My subscription.');
        } else if (error.status === 403 && code === 'SUBSCRIPTION_ENDED') {
          sessionStorage.clear();
          this.router.navigate(['/login'], { queryParams: { reason: 'ended' } });
        } else if (error.status === 403 && code === 'ACTIVATION_PENDING_READ_ONLY') {
          // SA-23: before activation only basic data can be changed; the screen stays open.
          this.toastr.warning(this.langService.getCurrent() === 'ar'
            ? 'شركتك غير مفعّلة بعد: يمكنك تعديل البيانات الأساسية فقط، وهذه الشاشة للمشاهدة حتى التفعيل.'
            : 'Your company is not active yet: you can change basic data only; this screen is view-only until activation.');
        } else if (error.status === 403 && code === 'ACTIVATION_PENDING') {
          // SA-14: the restricted session outlived the activation (the company is live now): the activation page upgrades it.
          if (!this.router.url.startsWith('/activation')) this.router.navigateByUrl('/activation');
        } else if (error.status === 403 && (code === 'COMPANY_CLOSED' || code === 'COMPANY_SUSPENDED')) {
          // SA-07: a suspended company is cut off like a closed one, with its own message.
          sessionStorage.clear();
          this.router.navigate(['/login'], { queryParams: { reason: code === 'COMPANY_SUSPENDED' ? 'suspended' : 'closed' } });
        } else if (error.status === 403 && code === 'NO_ENTITLEMENT') {
          this.notifyNoEntitlement((error.error as any)?.system || '');
          this.refreshEntitlements();
        } else if (error.status === 403 && code === 'MODULE_READ_ONLY') {
          // SA-17: a module the plan no longer includes can be viewed (until a date) but not changed.
          const until = String((error.error as any)?.readOnlyUntil ?? '').slice(0, 10);
          this.toastr.warning(this.langService.getCurrent() === 'ar'
            ? `أُزيلت هذه الوحدة من خطتكم: يمكنكم عرض بياناتها حتى ${until} دون تعديلها. قوموا بالترقية للعمل عليها مجددًا.`
            : `This module was removed from your plan: you can view its data until ${until} but not change it. Upgrade to work in it again.`);
        } else if (error.status === 403 && code === 'SCREEN_PERMISSION') {
          // The role lacks this screen's action (HR, Projects, CRM and Documents check it on the server since 2026-10-13).
          this.notifyScreenPermission();
        } else if (error.status === 429) {
          this.notifyRateLimited(Number(error.headers?.get('Retry-After')) || (error.error as any)?.retryAfter);
        }
        if (error.status === 401) {
          // Token expired or unauthorized — logout. SEC-02: a signed-out, revoked or idle device, or a network the company does not
          // allow, says why (the sign-in page tells the user).
          sessionStorage.clear();
          const reason = ['SESSION_REVOKED', 'SESSION_IDLE', 'SESSION_ENDED', 'IP_NOT_ALLOWED'].includes(code) ? code : undefined;
          this.router.navigate(['/login'], {
            queryParams: reason ? { returnUrl: this.router.url, reason } : { returnUrl: this.router.url },
          });
        }
        return throwError(() => error);
      })
    );
  }

  /** The role does not allow this on the screen: one warning per burst of refused calls, instead of a silent failure. */
  private notifyScreenPermission() {
    const now = Date.now();
    if (now - this.lastScreenPermissionToast < 5000) return;
    this.lastScreenPermissionToast = now;
    this.toastr.warning(this.langService.getCurrent() === 'ar'
      ? 'دورك لا يسمح بهذا الإجراء في هذه الشاشة. اطلب من مدير الشركة إضافة الصلاحية لدورك.'
      : 'Your role does not allow this on this screen. Ask your company manager to add the permission to your role.');
  }

  /** Too many attempts (sign-in, codes, password reset): one "wait" warning instead of a raw error. */
  private notifyRateLimited(retryAfter?: number) {
    const now = Date.now();
    if (now - this.lastRateLimitToast < 5000) return;
    this.lastRateLimitToast = now;

    const seconds = retryAfter && retryAfter > 0 ? Math.ceil(retryAfter) : 60;
    const message = this.langService.getCurrent() === 'ar'
      ? `محاولات كثيرة. يرجى الانتظار ${seconds} ثانية ثم المحاولة مرة أخرى.`
      : `Too many attempts. Please wait ${seconds} seconds and try again.`;
    this.toastr.warning(message);
  }

  /** The plan lacks the module behind this endpoint: show one "upgrade" warning instead of a raw error. */
  private notifyNoEntitlement(system: string) {
    const now = Date.now();
    if (now - (this.lastNoEntitlementToast[system] ?? 0) < 5000) return;
    this.lastNoEntitlementToast[system] = now;

    const message = this.langService.getCurrent() === 'ar'
      ? `باقة الاشتراك الحالية لا تشمل وحدة "${system}". يرجى ترقية الاشتراك.`
      : `Your subscription plan does not include the "${system}" module. Please upgrade your subscription.`;
    this.toastr.warning(message);
  }
}
